import React, { useMemo } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';
import { DateRangePreset, getDateRangeForPreset } from '../../utils/dateRangeUtils';
import { cn } from '../../utils';

interface QuickDateRangeProps {
  onRangeChange: (from: string, to: string, preset: DateRangePreset) => void;
  selectedPreset: DateRangePreset;
  label?: string;
  className?: string;
}

export const QuickDateRange: React.FC<QuickDateRangeProps> = ({
  onRangeChange,
  selectedPreset,
  label,
  className,
}) => {
  const { translate } = useTranslation();

  const presetOptions = useMemo(
    () => [
      {
        value: 'yesterday',
        label: translate('pos.orders_report.preset_yesterday'),
      },
      {
        value: 'today',
        label: translate('pos.orders_report.preset_today'),
      },
      {
        value: 'thisWeek',
        label: translate('pos.orders_report.preset_this_week'),
      },
      {
        value: 'thisMonth',
        label: translate('pos.orders_report.preset_this_month'),
      },
      {
        value: 'currentFiscalMonth',
        label: translate('pos.orders_report.preset_current_fiscal_month'),
      },
      {
        value: 'previousFiscalMonth',
        label: translate('pos.orders_report.preset_previous_fiscal_month'),
      },
      {
        value: 'lastWeek',
        label: translate('pos.orders_report.preset_last_week'),
      },
      {
        value: 'lastMonth',
        label: translate('pos.orders_report.preset_last_month'),
      },
      {
        value: 'thisYear',
        label: translate('pos.orders_report.preset_this_year'),
      },
      {
        value: 'lastYear',
        label: translate('pos.orders_report.preset_last_year'),
      },
      {
        value: 'custom',
        label: translate('pos.orders_report.preset_custom'),
      },
    ],
    [translate]
  );

  const handlePresetChange = (value: string) => {
    const preset = value as DateRangePreset;
    if (preset !== 'custom') {
      const { from, to } = getDateRangeForPreset(preset);
      onRangeChange(from, to, preset);
    } else {
      onRangeChange('', '', 'custom');
    }
  };

  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor="quick-date-preset">
        {label || translate('pos.orders_report.quick_select')}
      </Label>
      <Select value={selectedPreset} onValueChange={handlePresetChange}>
        <SelectTrigger id="quick-date-preset" className="w-full">
          <SelectValue placeholder={translate('pos.orders_report.quick_select')} />
        </SelectTrigger>
        <SelectContent>
          {presetOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
